// src/components/newsletterForms.ts
import { getAttributionForForms } from '../utils/attribution';

class NewsletterForm {
  private form!: HTMLFormElement;
  private submitButton!: HTMLButtonElement;
  private isSubmitting: boolean = false;
  private apiUrl: string = '';
  // Jeton reCAPTCHA validé dans la modale, envoyé au backend qui le vérifie (revue sécurité 09/2026)
  private recaptchaToken: string = '';
  private static instance: NewsletterForm | null = null;

  constructor() {
      // Pattern Singleton pour éviter les doubles initialisations
      if (NewsletterForm.instance) {
          console.log("NewsletterForm déjà initialisé");
          return NewsletterForm.instance;
      }

      this.form = document.getElementById('newsletterForm') as HTMLFormElement;
      if (!this.form) {
          console.error("Formulaire de newsletter non trouvé dans le DOM");
          return;
      }

      this.submitButton = this.form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (!this.submitButton) {
          this.submitButton = this.form.querySelector('button') as HTMLButtonElement;
      }

      // Utiliser l'API backend au lieu d'appeler directement Brevo
      this.apiUrl = import.meta.env.VITE_API_URL || 'https://forms.cryptoaccounting.fr';
      console.log("✓ API URL configurée:", this.apiUrl);

      this.initialize();
      NewsletterForm.instance = this;
  }

  private async initialize(): Promise<void> {
      console.log("Initialisation du formulaire de newsletter");

      // Nous allons transformer l'événement submit pour afficher le reCAPTCHA
      this.form.addEventListener('submit', async (e) => {
          e.preventDefault();
          this.clearAllErrors();

          // Validation des champs
          const nameInput = this.form.querySelector('input[name="name"]') as HTMLInputElement;
          const emailInput = this.form.querySelector('input[type="email"]') as HTMLInputElement;

          let hasErrors = false;

          // Validation du nom (requis)
          if (nameInput && !this.validateName(nameInput)) {
              hasErrors = true;
          }

          // Validation de l'email
          if (!emailInput || !this.validateEmail(emailInput)) {
              hasErrors = true;
          }

          if (hasErrors) {
              return;
          }

          // Afficher le modal reCAPTCHA
          await this.showRecaptchaModal();
      });

      // Validation au blur uniquement (quand l'utilisateur quitte le champ après avoir tapé)
      const emailInput = this.form.querySelector('input[type="email"]') as HTMLInputElement;
      const nameInput = this.form.querySelector('input[name="name"]') as HTMLInputElement;

      if (emailInput) {
          let hasTyped = false;
          emailInput.addEventListener('input', () => {
              hasTyped = true;
              // Retirer l'erreur pendant la saisie
              emailInput.classList.remove('is-invalid');
              this.removeErrorMessage(emailInput);
          });
          emailInput.addEventListener('blur', () => {
              if (hasTyped && emailInput.value.trim()) {
                  this.validateEmail(emailInput);
              }
          });
      }

      if (nameInput) {
          let hasTyped = false;
          nameInput.addEventListener('input', () => {
              hasTyped = true;
              nameInput.classList.remove('is-invalid');
              this.removeErrorMessage(nameInput);
          });
          nameInput.addEventListener('blur', () => {
              if (hasTyped && nameInput.value.trim()) {
                  this.validateName(nameInput);
              }
          });
      }

      // reCAPTCHA chargé à la première interaction avec le formulaire, pas à l'ouverture de la page
      const { loadRecaptchaOnInteraction } = await import('../utils/recaptcha');
      loadRecaptchaOnInteraction(this.form);
  }

  private validateName(field: HTMLInputElement): boolean {
    const value = field.value.trim();
    const isValid = value.length >= 2;

    field.classList.remove('is-invalid');
    this.removeErrorMessage(field);

    if (!isValid) {
        field.classList.add('is-invalid');
        this.showFieldError('Veuillez entrer votre nom');
    }

    return isValid;
  }

  private clearAllErrors(): void {
    const errorElement = document.getElementById('formError');
    if (errorElement) {
        errorElement.textContent = '';
    }

    this.form.querySelectorAll('input').forEach(input => {
        input.classList.remove('is-invalid');
        this.removeErrorMessage(input);
    });
  }

  private showFieldError(message: string): void {
    const errorElement = document.getElementById('formError');
    if (errorElement && !errorElement.textContent) {
        errorElement.textContent = message;
    }
  }

  private validateEmail(field: HTMLInputElement): boolean {
    const value = field.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = value.length > 0 && emailRegex.test(value);

    field.classList.remove('is-invalid');
    this.removeErrorMessage(field);

    if (!isValid) {
        field.classList.add('is-invalid');
        if (!value) {
            this.showFieldError('Veuillez entrer votre adresse email');
        } else {
            this.showFieldError('Veuillez entrer une adresse email valide (exemple@domaine.com)');
        }
    }

    return isValid;
  }

  // Ajouter cette méthode pour afficher le modal avec reCAPTCHA
  private async showRecaptchaModal(): Promise<void> {
    // Créer l'overlay du modal
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

    // Créer le contenu du modal
    const modal = document.createElement('div');
    modal.className = 'bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4 relative';

    // En-tête du modal
    const header = document.createElement('div');
    header.className = 'mb-4 text-center';
    header.innerHTML = `
        <h3 class="text-xl font-semibold text-primary-900">Vérification de sécurité</h3>
        <p class="text-gray-600 mt-2">Pour finaliser votre inscription, merci de confirmer que vous n'êtes pas un robot.</p>
    `;

    // Container pour le reCAPTCHA
    const recaptchaContainer = document.createElement('div');
    recaptchaContainer.className = 'flex justify-center my-4';

    // Widget reCAPTCHA - Utilisez un ID unique pour le conteneur
    const recaptchaWidget = document.createElement('div');
    recaptchaWidget.id = 'newsletter-recaptcha-widget';
    recaptchaWidget.className = 'g-recaptcha';

    // Message d'erreur pour reCAPTCHA
    const errorDiv = document.createElement('div');
    errorDiv.id = 'recaptcha-newsletter-error';
    errorDiv.className = 'hidden text-red-500 text-sm text-center mt-2';
    errorDiv.textContent = 'Veuillez confirmer que vous n\'êtes pas un robot';

    // Bouton de soumission
    const submitButton = document.createElement('button');
    submitButton.className = 'btn-primary w-full mt-4';
    submitButton.textContent = 'Valider et s\'inscrire';

    // Bouton de fermeture
    const closeButton = document.createElement('button');
    closeButton.className = 'absolute top-3 right-3 text-gray-400 hover:text-gray-600';
    closeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
    `;

    // Assembler le modal
    recaptchaContainer.appendChild(recaptchaWidget);
    modal.appendChild(closeButton);
    modal.appendChild(header);
    modal.appendChild(recaptchaContainer);
    modal.appendChild(errorDiv);
    modal.appendChild(submitButton);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Le script peut être encore en cours de chargement (chargé à la première interaction)
    const { loadRecaptchaScript } = await import('../utils/recaptcha');
    await loadRecaptchaScript();

    // IMPORTANT: Rendre le reCAPTCHA après avoir ajouté le widget au DOM
    try {
        // @ts-ignore
        grecaptcha.render('newsletter-recaptcha-widget', {
            'sitekey': import.meta.env.VITE_RECAPTCHA_SITE_KEY,
            'callback': function(response: string) {
                // Cacher le message d'erreur quand reCAPTCHA est validé
                if (response) {
                    errorDiv.classList.add('hidden');
                }
            }
        });
    } catch (error) {
        console.error("Erreur lors du rendu du reCAPTCHA:", error);
    }

    // Gérer les événements
    return new Promise((resolve) => {
        closeButton.addEventListener('click', () => {
            document.body.removeChild(overlay);
            resolve();
        });

        submitButton.addEventListener('click', async () => {
            try {
                // @ts-ignore
                const response = grecaptcha.getResponse();

                if (!response || response.length === 0) {
                    errorDiv.classList.remove('hidden');
                    return;
                }

                // Cacher le message d'erreur si reCAPTCHA est valide
                errorDiv.classList.add('hidden');
                this.recaptchaToken = response;

                // Fermer le modal
                document.body.removeChild(overlay);

                // Soumettre le formulaire
                this.handleSubmit(new Event('submit'));
            } catch (error) {
                console.error("❌ Erreur lors de la vérification du reCAPTCHA:", error);
                errorDiv.classList.remove('hidden');
            }
        });
    });
}

  private async handleSubmit(e: Event): Promise<void> {
      e.preventDefault();

      if (this.isSubmitting) return;

      console.log("⚙️ Soumission du formulaire de newsletter...");

      // Validation des champs
      const emailInput = this.form.querySelector('input[type="email"]') as HTMLInputElement;

      if (!emailInput) {
          console.error("Champ email non trouvé dans le formulaire");
          return;
      }

      if (!this.validateEmail(emailInput)) {
          this.showToast('Veuillez corriger les erreurs dans le formulaire', 'error');
          return;
      }

      const formData = new FormData(this.form);
      const subscriberEmail = formData.get("email") as string;
      const subscriberName = formData.get("name") as string || '';
      const subscriberCompany = formData.get("company") as string || '';

      try {
          this.isSubmitting = true;
          this.updateSubmitButton(true);

          // Appeler l'API backend pour la newsletter au lieu de Brevo directement
          const endpoint = `${this.apiUrl}/api/newsletter/subscribe`;

          console.log(`📧 Inscription newsletter via: ${endpoint}`);

          const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json"
              },
              body: JSON.stringify({
                  email: subscriberEmail,
                  name: subscriberName,
                  company: subscriberCompany,
                  source: 'Website Newsletter Blog',
                  recaptchaToken: this.recaptchaToken,
                  ...getAttributionForForms()
              })
          });

          const result = await response.json();

          if (!response.ok) {
              console.error('Erreur API newsletter:', result);
              throw new Error(result.error || "Une erreur est survenue lors de l'inscription");
          }

          console.log("✓ Inscription newsletter réussie:", result);

          // Réinitialiser le formulaire en cas de succès
          this.form.reset();
          this.clearAllErrors();

          this.showToast('Inscription réussie ! Un email de confirmation vous a été envoyé.', 'success');

      } catch (error) {
          console.error("❌ Erreur:", error);
          this.showToast(error instanceof Error ? error.message : 'Une erreur est survenue. Veuillez réessayer.', 'error');
      } finally {
          this.isSubmitting = false;
          this.updateSubmitButton(false);
      }
  }

  private updateSubmitButton(isLoading: boolean): void {
      this.submitButton.disabled = isLoading;
      this.submitButton.innerHTML = isLoading ?
          '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Inscription...' :
          'S\'abonner';
  }

  private removeErrorMessage(field: HTMLElement): void {
      const errorDiv = field.parentElement?.querySelector('.error-message');
      if (errorDiv) {
          errorDiv.remove();
      }
  }

  private showToast(message: string, type: 'success' | 'error'): void {
      const toast = document.createElement('div');
      toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white transform transition-all duration-300 translate-y-0 opacity-0 z-50 ${
          type === 'success' ? 'bg-green-500' : 'bg-red-500'
      }`;
      toast.textContent = message;
      document.body.appendChild(toast);

      setTimeout(() => {
          toast.classList.remove('translate-y-0', 'opacity-0');
      }, 100);

      setTimeout(() => {
          toast.classList.add('translate-y-2', 'opacity-0');
          setTimeout(() => {
              toast.remove();
          }, 300);
      }, 3000);
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('newsletterForm')) {
    try {
      // @ts-ignore
      const _newsletterForm = new NewsletterForm();
      console.log("✓ Formulaire de newsletter initialisé avec succès");
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation du formulaire de newsletter:", error);
    }
  }
});

export default NewsletterForm;
